import React, { useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import Icon from "@ats/src/components/shared/Icon";
import Tooltip from "@ats/src/components/shared/Tooltip";
import { INTERNAL_UTM_PARAMS } from "@shared/lib/utils";

interface UpdateAiCreditSubscriptionConfirmModalProps {
  onCancel: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isDowngrade: boolean;
  newPlanName: string;
  newPlanCredits: number;
  newMonthlyPrice: string;
  startDate: string;
  paymentMethodLabel: string;
  amountDueToday: string;
  currentPlanName: string;
  creditForCurrentPlan: string;
  currentPlanCredits: number | null;
}

function UpdateAiCreditSubscriptionConfirmModal(
  props: UpdateAiCreditSubscriptionConfirmModalProps,
) {
  const {
    onCancel,
    onConfirm,
    isLoading,
    isDowngrade,
    newPlanName,
    newPlanCredits,
    newMonthlyPrice,
    startDate,
    paymentMethodLabel,
    amountDueToday,
    currentPlanName,
    creditForCurrentPlan,
    currentPlanCredits,
  } = props;

  const [showDetails, setShowDetails] = useState(false);

  return (
    <CenterModal headerTitleText="Confirm your update" onCancel={onCancel}>
      <Styled.PlanRow>
        <Styled.PlanName>{newPlanName}</Styled.PlanName>
        <Styled.PlanCredits>{newPlanCredits.toLocaleString()} credits per month</Styled.PlanCredits>
      </Styled.PlanRow>

      {isDowngrade && (
        <Styled.ScheduledNote>
          Your subscription changes to {newPlanName} at the end of your current billing period on{" "}
          {startDate}. You keep your current credits until then, and from that date you&rsquo;ll
          receive {newPlanCredits.toLocaleString()} credits each month
          {currentPlanCredits ? ` instead of ${currentPlanCredits.toLocaleString()}` : ""}.
        </Styled.ScheduledNote>
      )}

      <Styled.PayRow>
        <Styled.PayLabel>What you&rsquo;ll pay monthly starting {startDate}</Styled.PayLabel>
        <Styled.PayValue>
          <Styled.Price data-testid="monthly-price">
            {newMonthlyPrice}
          </Styled.Price>
          {isDowngrade && (
            <Styled.Card>
              {paymentMethodLabel}
              <Tooltip label="To add a new payment method, go to Manage Billing.">
                <Icon name="info" />
              </Tooltip>
            </Styled.Card>
          )}
        </Styled.PayValue>
      </Styled.PayRow>

      {!isDowngrade && (
        <>
          <Styled.Divider />

          {showDetails && (
            <Styled.Details>
              <Styled.DetailRow>
                <span>{newPlanName} plan</span>
                <span className="num">{newMonthlyPrice}</span>
              </Styled.DetailRow>
              <Styled.DetailRow>
                <span>Credit for current {currentPlanName} plan</span>
                <span className="num">{creditForCurrentPlan}</span>
              </Styled.DetailRow>
              <Styled.DetailDivider />
              <Styled.DetailRow className="total">
                <span>Total</span>
                <span className="num">{amountDueToday}</span>
              </Styled.DetailRow>
            </Styled.Details>
          )}

          <Styled.DueRow>
            <span>Amount due today</span>
            <span className="num" data-testid="amount-due-today">{amountDueToday}</span>
          </Styled.DueRow>

          <Styled.MetaRow>
            <Styled.Toggle type="button" onClick={() => setShowDetails((v) => !v)}>
              {showDetails ? "Hide details" : "View details"}
            </Styled.Toggle>
            <Styled.Card>
              {paymentMethodLabel}
              <Tooltip label="To add a new payment method, go to Manage Billing.">
                <Icon name="info" />
              </Tooltip>
            </Styled.Card>
          </Styled.MetaRow>
        </>
      )}

      <Styled.Terms>
        By confirming, you agree to Polymer&rsquo;s <a href={`https://polymer.co/terms${INTERNAL_UTM_PARAMS}`} target="_blank" rel="noopener noreferrer">Terms of Service</a> and{" "}
        <a href={`https://polymer.co/privacy${INTERNAL_UTM_PARAMS}`} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      </Styled.Terms>

      <Styled.ButtonContainer>
        <Button onClick={onConfirm} loading={isLoading}>
          {isDowngrade ? "Confirm change" : "Confirm"}
        </Button>
        <Button styleType="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </Styled.ButtonContainer>
    </CenterModal>
  );
}

export default UpdateAiCreditSubscriptionConfirmModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.PlanRow = styled.div(() => {
  return css`
    label: UpdateSubscriptionConfirmModal_PlanRow;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 14px;
  `;
});

Styled.PlanName = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_PlanName;
    ${[t.text.md, t.text.bold]}
    color: ${t.dark ? t.color.gray[100] : t.color.black};
  `;
});

Styled.PlanCredits = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_PlanCredits;
    ${[t.text.sm, t.text.medium]}
    color: ${t.dark ? t.color.gray[300] : t.color.gray[700]};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  `;
});

Styled.ScheduledNote = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_ScheduledNote;
    ${[t.text.sm, t.mt(2)]}
    line-height: 1.55;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.PayRow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_PayRow;
    ${[t.mt(4)]}
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
  `;
});

Styled.PayLabel = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_PayLabel;
    ${[t.text.sm]}
    line-height: 1.45;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.PayValue = styled.div(() => css`
  label: UpdateSubscriptionConfirmModal_PayValue;
  text-align: right;
  flex: 0 0 auto;
`);

Styled.Price = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_Price;
    ${[t.text.md, t.text.bold]}
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
    color: ${t.dark ? t.color.gray[100] : t.color.black};
    font-variant-numeric: tabular-nums;

    svg {
      width: 13px;
      height: 13px;
      color: ${t.color.gray[500]};
    }
  `;
});

Styled.Card = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_Card;
    ${[t.text.sm, t.mt(2)]}
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

    svg {
      width: 13px;
      height: 13px;
    }
  `;
});

Styled.Divider = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_Divider;
    height: 1px;
    ${[t.my(4)]}
    background: ${t.dark ? t.color.gray[700] : t.color.gray[200]};
  `;
});

Styled.Details = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_Details;
    ${[t.rounded.sm, t.px(4), t.py(3), t.mb(4)]}
    background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
    display: flex;
    flex-direction: column;
    gap: 11px;
  `;
});

Styled.DetailRow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_DetailRow;
    ${[t.text.sm]}
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    color: ${t.dark ? t.color.gray[300] : t.color.gray[700]};

    .num {
      font-variant-numeric: tabular-nums;
    }

    &.total {
      ${[t.text.bold]}
      color: ${t.dark ? t.color.gray[100] : t.color.black};
    }
  `;
});

Styled.DetailDivider = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_DetailDivider;
    height: 1px;
    background: ${t.dark ? t.color.gray[600] : t.color.gray[200]};
  `;
});

Styled.DueRow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_DueRow;
    ${[t.text.md, t.text.bold]}
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    color: ${t.dark ? t.color.gray[100] : t.color.black};

    .num {
      font-variant-numeric: tabular-nums;
    }
  `;
});

Styled.MetaRow = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_MetaRow;
    ${[t.mt(2)]}
    display: flex;
    justify-content: space-between;
    align-items: center;
  `;
});

Styled.Toggle = styled.button((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_Toggle;
    ${[t.text.sm]}
    appearance: none;
    background: transparent;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.Terms = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_Terms;
    ${[t.text.xs, t.mt(5)]}
    line-height: 1.5;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

    a {
      color: ${t.dark ? t.color.gray[200] : t.color.black};
      text-decoration: underline;
    }
  `;
});

Styled.ButtonContainer = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: UpdateSubscriptionConfirmModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}
      &:last-of-type {
        ${t.mr(0)}
      }
    }
    ${t.mt(6)}
  `;
});
